<template>
  <div class="min-h-screen relative overflow-hidden bg-black keyboard-container">
    
    <!-- White Top Bar -->
    <div class="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
      <div class="flex items-center justify-center py-4 px-4">
        <img 
          src="/images/logo.png" 
          alt="Saskatoon Auto Connection" 
          class="h-14 w-auto object-contain"
          @error="handleTopBarImageError"
        />
      </div>
    </div>
    
    
    <!-- Main Content Container -->
    <div class="relative z-10 min-h-screen flex flex-col items-center justify-center px-3" style="padding-top: 5rem; padding-bottom: 5rem;">
      <div class="text-center max-w-lg mx-auto w-full">

        <!-- AI Empathy Response -->
        <div v-if="aiResponse" class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 mb-6 mt-8 border border-white border-opacity-20 animate-fade-in">
          <div class="flex items-start space-x-4">
            <div class="w-10 h-10 bg-orange-500 bg-opacity-20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-gray-200 text-sm leading-relaxed">{{ aiResponse }}</p>
            </div>
          </div>
        </div>

        <!-- Resolution Offer -->
        <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 mb-6 mt-8 border border-white border-opacity-20 animate-fade-in">
          <h2 class="text-xl font-semibold text-white mb-4 glow-text">
            Let us help resolve this immediately
          </h2>
          <p class="text-gray-300 text-sm mb-6">
            We take every concern seriously and want to make sure you're completely satisfied with our service.
          </p>
        
          <!-- Manager Selection -->
          <div class="space-y-6">
            <h3 class="font-medium text-white mb-4">Choose who you'd like to speak with:</h3>
            <!-- Debug info (temporary) -->
            <div class="text-xs text-gray-400 mb-2">Selected: {{ selectedManager }}</div>
          
            <div class="grid grid-cols-1 gap-4">
              <!-- Brynn -->
              <label class="relative">
                <input
                  type="radio"
                  name="manager"
                  value="brynn"
                  v-model="selectedManager"
                  class="sr-only"
                />
                <div class="bg-gray-200 rounded-2xl p-6 cursor-pointer transition-all duration-200 border-2"
                     :class="[
                       selectedManager === 'brynn' ? 'border-red-500' : 'border-gray-300',
                       selectedManager !== 'brynn' ? 'hover:border-red-400' : ''
                     ]">
                  <div class="flex items-center space-x-4">
                    <div class="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0">
                      <img 
                        src="/images/Brynn.jpg" 
                        alt="Brynn - Service Manager" 
                        class="w-full h-full object-cover"
                        @error="handleBrynnImageError"
                      />
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-gray-900">Brynn</h4>
                      <p class="text-gray-700 text-sm">Service Manager</p>
                      <p class="text-gray-600 text-xs">Specializes in service quality & technical issues</p>
                    </div>
                    <div v-if="selectedManager === 'brynn'" class="text-red-500">
                      <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                  </div>
              </div>
            </label>

              <!-- Dustin -->
              <label class="relative">
                <input
                  type="radio"
                  name="manager"
                  value="dustin"
                  v-model="selectedManager"
                  class="sr-only"
                />
                <div class="bg-gray-200 rounded-2xl p-6 cursor-pointer transition-all duration-200 border-2"
                     :class="[
                       selectedManager === 'dustin' ? 'border-red-500' : 'border-gray-300',
                       selectedManager !== 'dustin' ? 'hover:border-red-400' : ''
                     ]">
                  <div class="flex items-center space-x-4">
                    <div class="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0">
                      <img 
                        src="/images/Dustin.jpg" 
                        alt="Dustin - General Manager" 
                        class="w-full h-full object-cover"
                        @error="handleDustinImageError"
                      />
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-gray-900">Dustin</h4>
                      <p class="text-gray-700 text-sm">General Manager</p>
                      <p class="text-gray-600 text-xs">Handles complex issues & customer relations</p>
                    </div>
                    <div v-if="selectedManager === 'dustin'" class="text-red-500">
                      <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </label>
          </div>
        </div>
      </div>

        <!-- Detailed Complaint Form -->
        <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white border-opacity-20 animate-fade-in">
          <h3 class="font-semibold text-white mb-6 glow-text">Tell us more about the issue</h3>
          
          <div class="space-y-6">
            <!-- Detailed Description -->
            <div>
              <textarea
                ref="textareaRef"
                v-model="complaintDetails"
                class="w-full min-h-[140px] p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-gray-400 keyboard-input resize-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="What specifically went wrong? How can we improve? Any additional context that would help us understand the situation..."
                @focus="handleInputFocus"
                @blur="handleInputBlur"
              />
              <div class="mt-2 flex justify-end items-center">
                <span class="text-xs" :class="complaintDetails.trim().length > 5 ? 'text-green-400' : 'text-gray-500'">
                  {{ complaintDetails.trim().length }} characters (minimum 6 required)
                </span>
              </div>
            </div>

            <!-- Priority Level -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-3">
                How urgent is this issue?
              </label>
              <select v-model="priority" class="w-full p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white keyboard-input focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent" @focus="handleInputFocus" @blur="handleInputBlur">
                <option value="normal" class="text-gray-800">Normal - Can wait a day or two</option>
                <option value="urgent" class="text-gray-800">Urgent - Need response today</option>
                <option value="low" class="text-gray-800">Low priority - When convenient</option>
              </select>
            </div>

            <!-- Contact Information -->
            <div v-if="shouldShowContactForm" class="border-t border-white border-opacity-20 pt-6">
              <!-- Debug info -->
              <div class="text-xs text-yellow-300 mb-2">
                DEBUG: shouldShowContactForm={{ shouldShowContactForm }}, hasCustomerInfo={{ hasCustomerInfo }}
              </div>
              <h4 class="font-medium text-white mb-4">
                {{ hasCustomerInfo ? 'Contact Information (Optional)' : 'Contact Information' }}
              </h4>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm text-gray-300 mb-2">
                    Phone Number {{ hasCustomerInfo ? '' : '(Required)' }}
                  </label>
                  <input
                    type="tel"
                    v-model="contactPhone"
                    class="w-full p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-gray-400 keyboard-input focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    :class="{ 'border-red-400': !hasCustomerInfo && !contactPhone.trim() }"
                    placeholder="(306) 123-4567"
                    @focus="handleInputFocus"
                    @blur="handleInputBlur"
                  />
                  <div v-if="!hasCustomerInfo && !contactPhone.trim()" class="mt-1 text-xs text-red-400">
                    Phone number is required
                  </div>
                </div>
                <div v-if="hasCustomerInfo">
                  <label class="block text-sm text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    v-model="contactEmail"
                    class="w-full p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-gray-400 keyboard-input focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    placeholder="your.email@example.com"
                    @focus="handleInputFocus"
                    @blur="handleInputBlur"
                  />
                </div>
                <div v-else>
                  <label class="block text-sm text-gray-300 mb-2">
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    v-model="contactEmail"
                    class="w-full p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-gray-400 keyboard-input focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    placeholder="your.email@example.com"
                    @focus="handleInputFocus"
                    @blur="handleInputBlur"
                  />
                </div>
                <div>
                  <label class="block text-sm text-gray-300 mb-2">Best time to contact</label>
                  <select v-model="preferredTime" class="w-full p-4 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white keyboard-input focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent" @focus="handleInputFocus" @blur="handleInputBlur">
                    <option value="" class="text-gray-800">Any time</option>
                    <option value="morning" class="text-gray-800">Morning (8AM - 12PM)</option>
                    <option value="afternoon" class="text-gray-800">Afternoon (12PM - 5PM)</option>
                    <option value="evening" class="text-gray-800">Evening (5PM - 8PM)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Customer Info Notice (when URL has customer info) -->
            <div v-if="!shouldShowContactForm" class="border-t border-white border-opacity-20 pt-6">
              <div class="bg-blue-500 bg-opacity-20 p-4 rounded-lg border border-blue-400 border-opacity-20">
                <h4 class="font-medium text-blue-300 mb-2">Thank you, {{ customerName }}.</h4>
                <p class="text-sm text-blue-200">
                  We will do our best to address your concerns. We will contact you shortly.
                </p>
              </div>
            </div>

            <!-- Submit Button -->
            <button
              @click="submitComplaint"
              :disabled="!canSubmit || isLoading"
              class="w-full bg-red-500 hover:bg-red-600 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span v-if="isLoading" class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 20H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
              <span v-else>Submit to {{ selectedManagerName }}</span>
            </button>
            
            <!-- Help Text -->
            <div v-if="!canSubmit && !isLoading" class="mt-2 text-center">
              <p class="text-sm text-gray-400">
                {{ hasCustomerInfo 
                  ? 'Please describe your concern (minimum 6 characters)' 
                  : 'Please describe your concern (minimum 6 characters) and provide a phone number' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="bg-red-500 bg-opacity-20 rounded-2xl p-5 mb-6 border border-red-400 border-opacity-20 animate-fade-in">
          <p class="text-red-300 text-sm">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useFeedbackStore } from '@/stores/feedback'
import { submitComplaint as submitComplaintAPI } from '@/services/api'

// Track employee feedback
const trackEmployeeFeedback = async (employeeName: string, customerName: string, feedbackType: 'positive' | 'negative' | 'neutral', employeeType: 'service' | 'sales') => {
  try {
    await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName,
        customerName,
        type: employeeType,
        action: 'feedback',
        feedbackType
      })
    })
  } catch (error) {
    console.error('Failed to track employee feedback:', error)
  }
}

const router = useRouter()
const store = useFeedbackStore()

const selectedManager = ref<'brynn' | 'dustin'>('brynn')
const complaintDetails = ref('')
const priority = ref<'urgent' | 'normal' | 'low'>('normal')
const contactPhone = ref('')
const contactEmail = ref('')
const preferredTime = ref('')
const isLoading = ref(false)
const error = ref<string | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const isKeyboardOpen = ref(false)

const customerName = computed(() => store.customerName || '')
const aiResponse = computed(() => store.feedback.aiResponse)

const selectedManagerName = computed(() => {
  return selectedManager.value === 'brynn' ? 'Brynn' : 'Dustin'
})

const canSubmit = computed(() => {
  const hasComplaintDetails = selectedManager.value && complaintDetails.value.trim().length > 5
  
  // If customer info is available from URL, no contact info required
  if (hasCustomerInfo.value) {
    return hasComplaintDetails
  }
  
  // If no customer info, phone number is required
  return hasComplaintDetails && contactPhone.value.trim().length > 0
})

const shouldShowContactForm = computed(() => {
  // Hide contact form when customer info is available from URL
  return !hasCustomerInfo.value
})

const hasCustomerInfo = computed(() => {
  // Customer has info if name exists and is not a default value
  const result = customerName.value && customerName.value !== 'Welcome!' && customerName.value !== 'John'
  console.log('🔍 hasCustomerInfo computed:', {
    customerName: customerName.value,
    storeCustomerName: store.customerName,
    storeCustomer: store.customer,
    result
  })
  return result
})

// Handle top bar image error
function handleTopBarImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <h1 class="text-lg font-semibold text-gray-800">
        Saskatoon Auto Connection
      </h1>
    `
  }
}

// Handle Brynn image loading error - fallback to initial
function handleBrynnImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <div class="w-16 h-16 bg-orange-400 bg-opacity-20 rounded-2xl flex items-center justify-center">
        <span class="text-orange-400 font-semibold text-xl">B</span>
      </div>
    `
  }
}

// Handle Dustin image loading error - fallback to initial
function handleDustinImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <div class="w-16 h-16 bg-orange-400 bg-opacity-20 rounded-2xl flex items-center justify-center">
        <span class="text-orange-400 font-semibold text-xl">D</span>
      </div>
    `
  }
}

// Keyboard handling functions
function handleInputFocus(event: FocusEvent) {
  isKeyboardOpen.value = true
  // Small delay to ensure keyboard is fully open
  setTimeout(() => {
    const target = event.target as HTMLElement
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })
    }
  }, 300)
}

function handleInputBlur() {
  isKeyboardOpen.value = false
}

// Viewport change handler for additional keyboard detection
function handleViewportChange() {
  const currentHeight = window.innerHeight
  const initialHeight = window.screen.height
  const heightDifference = initialHeight - currentHeight
  
  // If height difference is significant, keyboard is likely open
  if (heightDifference > 150) {
    isKeyboardOpen.value = true
  } else {
    isKeyboardOpen.value = false
  }
}

onMounted(() => {
  // Pre-fill complaint details with original feedback text (exclude system messages)
  if (store.feedback.text && 
      !store.feedback.text.toLowerCase().includes('customer selected') &&
      !store.feedback.text.toLowerCase().includes('thank you')) {
    complaintDetails.value = store.feedback.text
  }
  
  // Pre-fill customer name if available
  if (customerName.value) {
    // Customer name is already available from store
  }
  
  // Add viewport change listener
  window.addEventListener('resize', handleViewportChange)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleViewportChange)
})

async function submitComplaint() {
  console.log('🔍 Submit complaint called, canSubmit:', canSubmit.value)
  
  if (!canSubmit.value) {
    console.log('❌ Cannot submit - validation failed')
    return
  }
  
  isLoading.value = true
  error.value = null
  
  try {
    console.log('📝 Preparing complaint data...')
    
    // Prepare complaint data
    const complaintData = {
      details: complaintDetails.value,
      priority: priority.value,
      manager: selectedManager.value,
      contact: {
        name: customerName.value || 'Customer', // Add customer name
        phone: contactPhone.value,
        email: contactEmail.value,
        preferredContact: contactPhone.value ? 'phone' : (contactEmail.value ? 'email' : 'phone'), // Determine preferred contact method
        preferredTime: preferredTime.value
      }
    }
    
    console.log('💾 Storing complaint data:', complaintData)
    console.log('🔍 Contact values:', {
      contactPhone: contactPhone.value,
      contactEmail: contactEmail.value,
      preferredTime: preferredTime.value,
      customerName: customerName.value
    })
    
    // Store complaint data
    store.setComplaint(complaintData)
    
    console.log('🚀 Submitting to API...', {
      customer: store.customer,
      feedback: store.feedback,
      complaint: complaintData
    })
    
    // Submit to API
    const response = await submitComplaintAPI(
      store.customer,
      store.feedback,
      complaintData
    )
    
    console.log('✅ API Response:', response)
    
    if (response.success) {
      // Store case number if provided
      if (response.caseNumber) {
        store.setComplaint({ ...complaintData, caseNumber: response.caseNumber })
      }
      
      // Track employee feedback (negative for complaints)
      if (store.customer.employeeName) {
        await trackEmployeeFeedback(store.customer.employeeName, store.customer.name, 'negative', 'service')
      }
      if (store.customer.salesPersonName) {
        await trackEmployeeFeedback(store.customer.salesPersonName, store.customer.name, 'negative', 'sales')
      }
      
      console.log('🎉 Success! Redirecting to thank-you page')
      router.push('/thank-you')
    } else {
      console.log('❌ API returned error:', response.message)
      error.value = response.message || 'Failed to submit complaint'
    }
  } catch (err) {
    console.error('❌ Complaint submission error:', err)
    error.value = 'Failed to submit complaint. Please try again.'
  } finally {
    isLoading.value = false
  }
}

</script>

<style scoped>
/* Liquid Background Animation from FeedbackForm */
.liquid-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 50%, rgba(0, 0, 0, 0.8) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(0, 0, 0, 0.6) 0%, transparent 50%),
              radial-gradient(circle at 50% 20%, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
  opacity: 1;
  transition: all 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(1.1);
}

.liquid-to-red {
  opacity: 0.8;
  transform: scale(1.1);
  background: radial-gradient(circle at 40% 60%, rgba(127, 29, 29, 0.8) 0%, rgba(127, 29, 29, 0.4) 70%),
              radial-gradient(circle at 60% 40%, rgba(153, 27, 27, 0.7) 0%, rgba(153, 27, 27, 0.3) 70%);
}

/* Text Glow Effect */
.glow-text {
  text-shadow: 
    0 0 10px rgba(255, 255, 255, 0.3),
    0 0 20px rgba(255, 255, 255, 0.2),
    0 0 30px rgba(255, 255, 255, 0.1);
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Keyboard handling */
.keyboard-container {
  transition: all 0.3s ease;
}

.keyboard-input {
  transition: all 0.2s ease;
  font-size: 16px !important; /* Prevent zoom on iOS */
}

/* iOS specific keyboard handling */
@supports (-webkit-touch-callout: none) {
  .keyboard-container {
    position: relative;
    overflow-x: hidden;
  }
  
  .keyboard-input:focus {
    transform: translateY(-8px);
  }
}

@media screen and (max-width: 768px) {
  .keyboard-input {
    font-size: 16px !important;
    transform-origin: center;
  }
}
</style> 